/**
 * Ledger primitives.
 *
 * Structure comes from surface and space: a group of things is a white card on
 * the tinted canvas, and one hairline divides the rows inside it. Soft radius,
 * one accent colour, one type family with tabular figures throughout.
 */

import React from 'react';
import { cn } from './cn';

/* ── Type scale helpers ──────────────────────────────────────────────────────
   Tracking and leading are part of the identity, so they travel with the size
   rather than being re-typed (and drifted) at each call site. */

export const TYPE = {
  // Plaster & Indigo. Three faces with strict jobs: Bricolage Grotesque for
  // headings and hero figures, Instrument Sans for UI and every figure, IBM
  // Plex Mono for labels, column heads and keys — metadata only, so mono
  // never competes with the numbers it sits beside.
  pageHeading: 'font-display text-[26px] sm:text-[30px] font-semibold tracking-[-0.02em] leading-[34px]',
  hero: 'font-display text-[44px] font-semibold tracking-[-0.025em] leading-[48px]',
  /** A hero figure — Bricolage, tabular. Tile numbers, payoff totals. */
  heroFigure: 'font-display text-[30px] font-semibold tracking-[-0.02em] leading-[32px] tabular-nums',
  formTitle: 'text-[17px] font-semibold leading-[22px]',
  bigNumber: 'text-[20px] font-semibold leading-none tabular-nums',
  /** A figure on a list row. Deliberately below `bigNumber`: a row is one of
   *  many, and should not read as loudly as the total above the list. */
  rowFigure: 'text-[14px] font-semibold leading-[17px] tabular-nums',
  rowTitle: 'text-[14px] font-medium leading-[17px]',
  subhead: 'text-[15px] leading-[22px]',
  body: 'text-[15px] leading-[22px]',
  small: 'text-[13px] leading-[18px]',
  /** Calendar cell rows — the densest type in the app. */
  cell: 'text-[12px] leading-[15px]',
  cellFigure: 'text-[12px] font-semibold leading-[15px] tabular-nums',

  navTab: 'text-[13px] font-medium',
  chip: 'text-[13px] font-medium',
  buttonLabel: 'text-[13px] font-semibold',
  /** Mono, caps, wide-tracked. Column heads, section eyebrows, keys. */
  sectionLabel: 'font-mono text-[10px] font-medium tracking-[0.1em] uppercase',
  /** Row metadata — dates, rates, counts. */
  meta: 'font-mono text-[11px] leading-[14px]',
  /** Field and stat labels. */
  metaTight: 'font-mono text-[11px] font-medium leading-[14px]',
  tag: 'font-mono text-[10px] font-semibold tracking-[0.06em] uppercase',
  index: 'font-mono text-[11px] font-medium tracking-[0.1em]',
} as const;

/* ── Page container ──────────────────────────────────────────────────────── */

export const Page: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('mx-auto w-full max-w-[1240px] px-5 sm:px-[44px] pb-[90px]', className)}>
    {children}
  </div>
);

/* ── PageHeader ──────────────────────────────────────────────────────────── */

interface PageHeaderProps {
  heading: string;
  /** `‹ Back to …` — rendered above the heading. */
  backLabel?: string;
  onBack?: () => void;
  /** Sits beside the title, typically a status Tag. */
  adornment?: React.ReactNode;
  subhead?: React.ReactNode;
  /** Pushed right and bottom-aligned with the text column. */
  actions?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  heading,
  backLabel,
  onBack,
  adornment,
  subhead,
  actions,
  className,
}) => (
  <div className={cn('pt-8', className)}>
    {backLabel && (
      <button
        type="button"
        onClick={onBack}
        className="text-[12px] font-medium text-ink-muted hover:text-accent transition-colors duration-[120ms] mb-4 block"
      >
        {'‹'} {backLabel}
      </button>
    )}
    <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
      <div className="max-w-[58ch] min-w-0">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className={cn(TYPE.pageHeading, 'text-ink')}>{heading}</h1>
          {adornment}
        </div>
        {subhead && <p className={cn(TYPE.subhead, 'text-ink-soft mt-3')}>{subhead}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  </div>
);

/* ── FormSection ─────────────────────────────────────────────────────────── */

interface FormSectionProps {
  title: string;
  /** `01`, `02`… Omit inside tab panels, where there is no sequence. */
  index?: string;
  /** Says what the section is for, and what can be skipped. */
  hint?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  index,
  hint,
  actions,
  children,
  className,
}) => (
  <section className={cn('bg-surface border border-rule-base rounded-xl p-6', className)}>
    <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
      <div className="max-w-[58ch] min-w-0">
        <div className="flex items-baseline gap-3">
          {index && <span className={cn(TYPE.index, 'text-accent')}>{index}</span>}
          <h2 className={cn(TYPE.formTitle, 'text-ink')}>{title}</h2>
        </div>
        {hint && <p className={cn(TYPE.small, 'text-ink-soft mt-2')}>{hint}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
    {children && <div className="mt-6">{children}</div>}
  </section>
);

/* ── SectionHeader (motif 2) ─────────────────────────────────────────────────
   For sub-parts nested INSIDE a row or block — never for top-level page
   sections, which would flatten the hierarchy. */

export const SectionHeader: React.FC<{
  label: string;
  note?: React.ReactNode;
  className?: string;
}> = ({ label, note, className }) => (
  <div className={cn('flex items-center gap-4', className)}>
    <span className={cn(TYPE.sectionLabel, 'text-ink-muted whitespace-nowrap')}>{label}</span>
    <span className="flex-1 h-px bg-rule-base" />
    {note && <span className={cn(TYPE.meta, 'text-ink-muted whitespace-nowrap')}>{note}</span>}
  </div>
);

/* ── SpecStrip + Stat ────────────────────────────────────────────────────── */

export const SpecStrip: React.FC<{
  /** Monospace facts, joined with ` · ` by the caller or passed as an array. */
  facts?: React.ReactNode[];
  children?: React.ReactNode;
  className?: string;
}> = ({ facts, children, className }) => (
  <div
    className={cn(
      'bg-surface border border-rule-base rounded-xl px-5 py-4 flex flex-wrap items-start justify-between gap-x-10 gap-y-5',
      className
    )}
  >
    {facts && facts.length > 0 && (
      <p className={cn(TYPE.meta, 'text-ink-muted leading-[1.8] flex-1 min-w-[200px]')}>
        {facts.map((f, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-rule-strong"> · </span>}
            {f}
          </React.Fragment>
        ))}
      </p>
    )}
    {children && <div className="flex flex-wrap items-start gap-x-10 gap-y-5">{children}</div>}
  </div>
);

export const Stat: React.FC<{
  value: React.ReactNode;
  label: string;
  /** Accent only when the figure means *now* or *needs you*. Red is reserved
   *  for a figure that is actually wrong. */
  accent?: boolean;
  className?: string;
}> = ({ value, label, accent, className }) => (
  <div className={cn('min-w-[92px]', className)}>
    <div className={cn(TYPE.bigNumber, accent ? 'text-accent' : 'text-ink')}>{value}</div>
    <div className={cn(TYPE.metaTight, 'text-ink-muted mt-1.5')}>{label}</div>
  </div>
);

/* ── Tag ─────────────────────────────────────────────────────────────────────
   Four weights. State is signalled by fill AND border, never colour alone. */

export type TagWeight = 'ink' | 'solid' | 'red' | 'redOutline' | 'bone';

const TAG_WEIGHTS: Record<TagWeight, string> = {
  ink: 'bg-accent-tint text-accent border border-accent-tint',
  // Inverse ink: emphasis that claims nothing — not interactive, not money.
  solid: 'bg-ink-soft text-surface border border-ink-soft',
  red: 'bg-bad-tint text-bad border border-bad',
  redOutline: 'bg-warn-tint text-warn border border-warn',
  bone: 'bg-surface text-ink-muted border border-rule-strong',
};

export const Tag: React.FC<{
  children: React.ReactNode;
  weight?: TagWeight;
  className?: string;
  title?: string;
}> = ({ children, weight = 'bone', className, title }) => (
  <span
    title={title}
    className={cn(
      TYPE.tag,
      'inline-flex items-center px-2 py-[3px] rounded-full whitespace-nowrap',
      TAG_WEIGHTS[weight],
      className
    )}
  >
    {children}
  </span>
);

/* ── Button ──────────────────────────────────────────────────────────────────
   `uppercase` must sit on the <button> itself: Preflight ships
   `button { text-transform: none }`, which beats an inherited uppercase. */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-surface border border-accent hover:bg-accent-press hover:border-accent-press',
  secondary: 'bg-surface text-ink-soft border border-rule-strong hover:border-ink-muted',
  ghost: 'bg-transparent text-ink-soft border border-transparent hover:text-accent',
  danger: 'bg-surface text-bad border border-bad hover:bg-bad hover:text-surface hover:border-bad',
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  className,
  children,
  ...rest
}) => (
  <button
    {...rest}
    className={cn(
      TYPE.buttonLabel,
      'px-4 py-[9px] rounded-lg transition-colors duration-[120ms] disabled:opacity-40 disabled:cursor-not-allowed',
      BUTTON_VARIANTS[variant],
      className
    )}
  >
    {children}
  </button>
);

/** Text-button for use inside lists and rails — monospace on a bone bottom
 *  rule that turns red on hover. Filled buttons are reserved for page headers
 *  and form footers. */
export const TextButton: React.FC<ButtonProps> = ({ className, children, ...rest }) => (
  <button
    {...rest}
    className={cn(
      // Not TYPE.meta: a text button is an action, not metadata, so it takes
      // the UI face. Under the old scale `meta` was merely a size and the
      // difference did not show; now that mono carries meaning, borrowing it
      // for a link said the wrong thing. It also set text-[11px] against the
      // text-[12.5px] beside it — a same-property collision that Tailwind
      // resolves by stylesheet order, not by the order written here.
      'text-[13px] font-medium text-accent hover:text-accent-press hover:underline underline-offset-2 transition-colors duration-[120ms] disabled:opacity-40',
      className
    )}
  >
    {children}
  </button>
);

/* ── Filter chip — active is INK fill, never red ─────────────────────────── */

export const Chip: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
> = ({ active, className, children, ...rest }) => (
  <button
    {...rest}
    className={cn(
      TYPE.chip,
      'px-3 py-[7px] rounded-lg border transition-colors duration-[120ms]',
      // Ternary, not two competing colour classes — see cn.ts.
      active
        ? 'bg-accent-tint text-accent border-accent'
        : 'bg-surface text-ink-soft border-rule-strong hover:border-ink-muted',
      className
    )}
  >
    {children}
  </button>
);

/* ── Fill strip (motif 3) — replaces progress bars ───────────────────────── */

export const FillStrip: React.FC<{
  filled: number;
  total: number;
  /** Red cells when the value means *now* or over capacity. */
  accent?: boolean;
  className?: string;
}> = ({ filled, total, accent, className }) => {
  const cells = Math.max(0, Math.min(40, Math.round(total)));
  const on = Math.max(0, Math.min(cells, Math.round(filled)));
  return (
    <div className={cn('flex gap-[3px] h-[18px]', className)} aria-hidden="true">
      {Array.from({ length: cells }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'flex-1',
            i < on
              ? accent
                ? 'bg-bad'
                : 'bg-accent'
              : 'bg-ground border border-rule-base'
          )}
        />
      ))}
    </div>
  );
};

/* ── Ink panel (motif 6) — once per page ─────────────────────────────────── */

export const InkPanel: React.FC<{
  label?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, children, className }) => (
  <div className={cn('bg-ink text-ondark rounded-xl p-6', className)}>
    {label && (
      <div className={cn(TYPE.sectionLabel, 'text-ondark-faint pb-3 mb-4 border-b border-ondark-rule')}>
        {label}
      </div>
    )}
    <div className={cn(TYPE.body, 'text-ondark-dim')}>{children}</div>
  </div>
);

/* ── Callouts (motifs 7 + 8) ─────────────────────────────────────────────── */

/** For something needing a decision. */
export const Callout: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('bg-bad-tint border border-bad rounded-xl px-5 py-4', className)}>
    {children}
  </div>
);

/** For a dated note or warning. */
export const RedNote: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('bg-warn-tint border-l-[3px] border-warn rounded-r-lg px-4 py-3', className)}>
    {children}
  </div>
);

/* ── Sticky bars ─────────────────────────────────────────────────────────────
   sticky fails silently if any ancestor has overflow:auto|hidden — check the
   whole chain before debugging one of these. */

export const StickyActionBar: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    className={cn(
      'sticky bottom-0 bg-ground border-t border-rule-base py-4 sticky-shadow flex flex-wrap items-center justify-end gap-3 z-20',
      className
    )}
  >
    {children}
  </div>
);

/* ── Empty state — a line of bone text, never an illustration ───────────── */

export const EmptyState: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <p className={cn(TYPE.meta, 'text-ink-muted py-10 text-center', className)}>{children}</p>
);

/* ── List scaffolding ────────────────────────────────────────────────────────
   A list opens and closes on ink; rows divide on hair. */

export const List: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('bg-surface border border-rule-base rounded-xl overflow-hidden', className)}>
    {children}
  </div>
);

export const Row: React.FC<{
  children: React.ReactNode;
  /** Red when the row is *now* or currently open; ink when normal. */
  accent?: 'red' | 'ink' | null;
  onClick?: () => void;
  className?: string;
}> = ({ children, accent = null, onClick, className }) => (
  <div
    onClick={onClick}
    className={cn(
      // The gutter lives here, not at each call site — every list had its rows
      // flush against the card edge while anything else in the card was inset.
      'px-4 border-b border-rule last:border-b-0 transition-colors duration-[120ms]',
      onClick && 'cursor-pointer hover:bg-ground',
      // One class only — competing border-l-* colours resolve by stylesheet
      // order, not by the order listed here. The accent rail eats one pixel of
      // the gutter, so pl matches the other side rather than adding to it.
      accent === 'red'
        ? 'border-l-[3px] border-l-bad pl-[13px]'
        : accent === 'ink'
          ? 'border-l-[3px] border-l-accent pl-[13px]'
          : '',
      className
    )}
  >
    {children}
  </div>
);
